import { useCallback, useEffect, useRef, useState } from 'react';
import type { JobDetail } from '@nest-recruit/shared';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { useSaveLane } from './save-lane';

/**
 * Autosave for one wizard step (06-qol §4: "autosave on any form longer than one
 * screen, with a visible Saved indicator and timestamp"; F5.13: resumable at any
 * step).
 *
 * The step keeps its own draft and never re-reads it from the server while the
 * person is typing — a save landing mid-keystroke would otherwise put back what they
 * typed a second ago. Saves run one at a time; a change made while one is in flight
 * waits for it and then goes with the version it returned, so two quick edits never
 * race each other into a 409.
 */

const SAVE_DELAY_MS = 800;

export type SaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'saved'; readonly at: Date }
  | { readonly kind: 'error'; readonly message: string; readonly fields: Readonly<Record<string, string>> };

interface Options<TDraft> {
  readonly job: JobDetail;
  readonly save: (draft: TDraft, version: number) => Promise<JobDetail>;
  /** A stale write: someone else saved first. The step reloads from `job`. */
  readonly onConflict: (message: string) => void;
}

export function useStepAutosave<TDraft>({ job, save, onConflict }: Options<TDraft>) {
  const [state, setState] = useState<SaveState>({ kind: 'idle' });
  const version = useRef(job.version);
  const queued = useRef<TDraft | null>(null);
  const isRunning = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveRef = useRef(save);
  const conflictRef = useRef(onConflict);
  // On JOB-13 every section shares one lane, so their saves take turns (R3).
  const lane = useSaveLane();

  useEffect(() => {
    saveRef.current = save;
    conflictRef.current = onConflict;
  });

  // A save from elsewhere (a realtime refetch, another tab) moves the version on;
  // follow it whenever nothing of ours is waiting to go.
  useEffect(() => {
    if (!isRunning.current && queued.current === null) version.current = job.version;
  }, [job.version]);

  const run = useCallback(async (): Promise<void> => {
    if (isRunning.current) return;
    isRunning.current = true;
    try {
      while (queued.current !== null) {
        const draft = queued.current;
        queued.current = null;
        setState({ kind: 'saving' });
        try {
          const saved = lane
            ? await lane.run((current) => saveRef.current(draft, current))
            : await saveRef.current(draft, version.current);
          version.current = saved.version;
          if (queued.current === null) setState({ kind: 'saved', at: new Date() });
        } catch (error) {
          if (error instanceof ApiError && error.code === 'stale_write') {
            queued.current = null;
            setState({ kind: 'idle' });
            conflictRef.current(error.message);
            return;
          }
          setState({
            kind: 'error',
            message: errorMessage(error),
            fields: error instanceof ApiError ? error.fields : {},
          });
          return;
        }
      }
    } finally {
      isRunning.current = false;
    }
  }, [lane]);

  /** Queues the draft and saves it once the person pauses. */
  const schedule = useCallback(
    (draft: TDraft): void => {
      queued.current = draft;
      setState({ kind: 'pending' });
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void run(), SAVE_DELAY_MS);
    },
    [run],
  );

  /** Saves now — leaving the step, or Ctrl+Enter. Resolves once nothing is waiting. */
  const flush = useCallback(async (): Promise<void> => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    await run();
  }, [run]);

  // Leaving the screen with a change still waiting saves it rather than losing it.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      if (queued.current !== null) void run();
    },
    [run],
  );

  // 06-qol §4: warn before a full navigation away would drop an unsaved change.
  useEffect(() => {
    const isDirty = state.kind === 'pending' || state.kind === 'saving';
    if (!isDirty) return undefined;
    const warn = (event: BeforeUnloadEvent): void => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [state.kind]);

  return { state, schedule, flush };
}
